import api from 'api/api';
import alertSignal from 'signals/Alert.signal';
import loaderSignal from 'signals/Loader.signal';
import organizationsListSignal, {
  organizationDetailsSignal,
  shareOrganizationSignal,
} from './OrganizationsList.signal';
import history from '../../../history';
import { camelCaseToTitleText } from 'utils/formatTxt';
import { getStatus } from 'libs/account';
import { titleCaseString } from 'libs/string';
import { DateTime } from 'luxon';
import $appSettings from 'signals/AppSettings.signal';

const BASE_ACCOUNT_INCLUDE = {
  _count: {
    select: {
      users: true,
    },
  },
  receivedUserInvites: true,
};

export const fetchVentureCapitalFirms = async (fundId) => {
  const { PORTAL_TYPES } = $appSettings.value.constants;
  const [fund] = await api.get({
    path: '/funds',
    options: {
      where: {
        id: fundId,
      },
      include: {
        fundingProgramMembership: {
          include: {
            ventureCapitalFirm: true,
            fundingProgram: true,
          },
        },
        vcPortfolioCompanies: {
          orderBy: xFormHistoryToOrderBy(PORTAL_TYPES.business),
          where: xFormHistoryToWhere(PORTAL_TYPES.business),
          include: {
            business: {
              include: {
                account: {
                  include: {
                    ...BASE_ACCOUNT_INCLUDE,
                  },
                },
              },
            },
          },
        },
      },
    },
  });

  const businesses = fund.vcPortfolioCompanies
    .map((vpc) => vpc.business)
    .flatMap((b) => b);

  return {
    businesses: businesses.map((b) => ({ ...b.account, business: b })),
    fund,
  };
};

export const fetchAndSetFundingProgramMemberships = async (
  fundingProgramMembershipId
) => {
  const { APPLICATION_STATUS, PORTAL_TYPES } = $appSettings.value.constants;

  const fundingProgramMembership = await api.get({
    path: '/fundingProgramMemberships',
    options: {
      where: {
        id: Number(fundingProgramMembershipId),
      },
      include: {
        lender: true,
        fundingProgram: true,
        applications: {
          orderBy: xFormHistoryToOrderBy(PORTAL_TYPES.business),
          where: xFormHistoryToWhere(PORTAL_TYPES.business),
          include: {
            business: {
              include: {
                account: {
                  include: {
                    ...BASE_ACCOUNT_INCLUDE,
                  },
                },
              },
            },
          },
        },
      },
    },
  });

  const { fundingProgram, lender } = fundingProgramMembership;
  const businesses = fundingProgramMembership.applications
    .filter((app) => app.status !== APPLICATION_STATUS.inProgress)
    .map((a) => a.business)
    .flatMap((b) => b);

  return {
    businesses: businesses.map((b) => ({ ...b.account, business: b })),
    fundingProgram,
    lender,
  };
};

export const handleOrganizationsFilterChange = ({ name, value }) => {
  const getViewType = (pathname) => {
    if (pathname.includes('/lenders')) return 'lenders';
    if (pathname.includes('/business')) return 'business';
    if (pathname.includes('/vc')) return 'vc';
    return null;
  };
  
  const currentViewType = getViewType(history.location.pathname);
  const currentSignal = organizationsListSignal.value;
  const updatedDropdownFilters = {
    ...currentSignal.dropdownFilters,
    [name]: value,
  };
  
  organizationsListSignal.update({
    dropdownFilters: updatedDropdownFilters,
    filtersByViewType: currentViewType ? {
      ...currentSignal.filtersByViewType,
      [currentViewType]: {
        dropdownFilters: updatedDropdownFilters,
        appliedFilters: currentSignal.appliedFilters,
      },
    } : currentSignal.filtersByViewType,
  });
};

export const handleOrganizationsAppliedFiltersChange = (appliedFilters) => {
  const getViewType = (pathname) => {
    if (pathname.includes('/lenders')) return 'lenders';
    if (pathname.includes('/business')) return 'business';
    if (pathname.includes('/vc')) return 'vc';
    return null;
  };
  
  const currentViewType = getViewType(history.location.pathname);
  const currentSignal = organizationsListSignal.value;
  
  organizationsListSignal.update({
    appliedFilters,
    viewType: currentViewType,
    filtersByViewType: currentViewType ? {
      ...currentSignal.filtersByViewType,
      [currentViewType]: {
        dropdownFilters: currentSignal.dropdownFilters,
        appliedFilters: appliedFilters,
      },
    } : currentSignal.filtersByViewType,
  });
};

export const resetOrganizationsFilterInputs = () =>
  organizationsListSignal.update({
    dropdownFilters: {},
  });

export const fetchAndSetOrganizations = async (page = 1) => {
  try {
    const pathname = history.location.pathname;
    const resourceId = pathname.split('/')[pathname.split('/').length - 1];
    const { recordsPerPage } = organizationsListSignal.value;

    let fundingProgram,
      lender,
      fund = {};
    let accounts = [];
    let totalPages = 1;
    let count = null;

    if (pathname.includes('fund')) {
      const data = await fetchVentureCapitalFirms();
      accounts = data?.businesses;
      fund = data?.fund;
    } else if (pathname.includes('program-membership')) {
      const data = await fetchAndSetFundingProgramMemberships(resourceId);
      accounts = data?.businesses;
      fundingProgram = data?.fundingProgram;
      lender = data?.lender;
    } else {
      const data = await fetchAccounts(page);
      accounts = data.accounts || data;
      totalPages = data.count ? Math.ceil(data.count / recordsPerPage) : 1;
      count = typeof data?.count === 'number' ? data.count : null;
    }

    return organizationsListSignal.update({
      accounts,
      count,
      fundingProgram,
      lender,
      fund,
      totalPages,
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      message: error.message,
      error,
    });
  }
};

export const fetchAccounts = async (page = 1) => {
  const { PORTAL_TYPES } = $appSettings.value.constants;
  const pathname = history.location.pathname;
  const { recordsPerPage } = organizationsListSignal.value;
  let portalType;
  if (pathname.includes('/edo/business')) {
    portalType = PORTAL_TYPES.business;
  }

  if (pathname.includes('/edo/vc')) {
    portalType = PORTAL_TYPES.vc;
  }

  if (pathname.includes('/edo/lender')) {
    portalType = PORTAL_TYPES.lender;
  }

  const include = {
    business: {
      include: {
        businessDocuments: true,
      },
    },
    lender: {
      include: {
        fundingProgramMemberships: {
          include: {
            fundingProgram: true,
          },
        },
      },
    },
    ventureCapitalFirm: {
      include: {
        fundingProgramMemberships: {
          include: {
            fundingProgram: true,
          },
        },
      },
    },
  };

  const whereClause = xFormHistoryToWhere(portalType);

  // For LENDER portal type, ensure we only get accounts that have lender records
  const where = { portalType, ...whereClause };
  if (portalType === PORTAL_TYPES.lender && Object.keys(whereClause).length === 0) {
    // If no filters, ensure lender is not null
    where.lender = { isNot: null };
  }

  let options = {
    getCount: true,
    skip: page ? (page - 1) * recordsPerPage : 0,
    take: recordsPerPage,
    include: {
      ...BASE_ACCOUNT_INCLUDE,
      ...include,
    },
    where,
    orderBy: xFormHistoryToOrderBy(portalType),
  };

  return api.get({
    path: '/accounts',
    options,
  });
};

export const fetchOrganization = async (organizationId, path) => {
  let options = {
    where: {
      id: Number(organizationId),
    },
  };

  const businessInclude = {
    businessDocuments: true,
    applications: {
      include: {
        fundingProgramMembership: {
          include: {
            fundingProgram: true,
            lender: true,
          },
        },
      },
    },
    vcPortfolioCompanies: {
      include: {
        fund: {
          include: {
            fundingProgramMembership: {
              include: {
                ventureCapitalFirm: true,
                fundingProgram: true,
              },
            },
          },
        },
      },
    },
  };

  if (path === '/businesses' || path === '/v2/businesses') {
    options.include = { ...options.include, ...businessInclude };
  }

  if (path === '/ventureCapitalFirms' || path === '/lenders') {
    options.include = {
      ...options.include,
      favoritedByBusinesses: true,
      fundingProgramMemberships: {
        include: {
          // Don't include applications - they're not needed for the detail view
          // Applications are only needed when viewing a specific program membership
          // This significantly improves performance for lenders with many applications
          fundingProgram: true,
          funds: true,
        },
      },
      programRequests: {
        include: {
          fundingProgram: true,
        },
      },
      account: {
        select: {
          portalType: true,
          ...(path === '/lenders' && {
            _count: { select: { users: true } },
          }),
        },
      },
    };
  } else {
    options.include = {
      ...options.include,
      favoritedByLenders: true,
      favoritedByVcs: true,
    };
  }

  loaderSignal.update({ isContentLoading: true });
  try {
    // v2 single-business endpoint: GET /api/v2/businesses/:id (returns one object; no array unwrap)
    const requestPath =
      path === '/v2/businesses'
        ? `/v2/businesses/${organizationId}`
        : path;

    const organization = await api.get({
      path: requestPath,
      options,
    });

    if (!organization) {
      return alertSignal.update({
        type: 'notification',
        message: 'Organization not found or you do not have access to view it.',
        error: new Error('Organization not found'),
      });
    }

    // List endpoints (e.g. GET /businesses with where.id) return an array; use first item for detail view. v2/businesses/:id returns a single object.
    const selectedOrganization =
      path === '/v2/businesses'
        ? organization
        : path === '/businesses' && Array.isArray(organization) && organization.length === 1
          ? organization[0]
          : organization;

    organizationDetailsSignal.update({ selectedOrganization });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      message: error.message,
      error,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const hideOrganizationDetailsModal = () => {
  organizationDetailsSignal.reset();
};

export const showOrganizationDetailsModal = (organization) => {
  organizationDetailsSignal.update({
    selectedOrganization: organization,
    modalVisible: true,
  });
};

const xFormHistoryToOrderBy = (portalType) => {
  const { PORTAL_TYPES } = $appSettings.value.constants;
  const params = Object.fromEntries(
    new URLSearchParams(history.location.search)
  );

  const { sort } = params;

  const orderBy = {
    organization: {},
  };

  if (sort) {
    const [key, dir] = sort.split('-');
    if (key === 'contactEmail' && portalType !== PORTAL_TYPES.business) {
      orderBy.organization['email'] = dir;
    } else {
      orderBy.organization[key] = dir;
    }
  } else {
    orderBy.organization.id = 'desc';
  }

  if (portalType === PORTAL_TYPES.business) {
    orderBy.business = orderBy.organization;
  } else if (portalType === PORTAL_TYPES.lender) {
    orderBy.lender = orderBy.organization;
  } else if (portalType === PORTAL_TYPES.vc) {
    orderBy.ventureCapitalFirm = orderBy.organization;
  }

  delete orderBy.organization;

  return orderBy;
};

const xFormHistoryToWhere = (portalType) => {
  const { PORTAL_TYPES } = $appSettings.value.constants;
  const where = {
    organization: {},
  };

  // Always read from URL params - this is the source of truth
  // URL params persist across navigation, signals may not
  let params = Object.fromEntries(
    new URLSearchParams(history.location.search)
  );

  // Helper to detect view type from pathname
  const getViewType = (pathname) => {
    if (pathname.includes('/lenders')) return 'lenders';
    if (pathname.includes('/business')) return 'business';
    if (pathname.includes('/vc')) return 'vc';
    return null;
  };
  
  // Filter keys that we care about (exclude page, sort, tab, etc.)
  const filterKeys = [
    'id',
    'name',
    'contactName',
    'contactEmail',
    'veteranOwned',
    'womanOwned',
    'minorityOwned',
  ];
  const hasFilterParams = filterKeys.some(key => params[key]);
  
  // Check if URL has filters but we're in a different view type
  // This handles the case where URL has filters from a previous view type
  const currentViewType = getViewType(history.location.pathname);
  const signalViewType = organizationsListSignal.value.viewType;
  const isViewTypeMismatch = signalViewType !== null && 
                             currentViewType !== null && 
                             signalViewType !== currentViewType;
  
  // If URL has filter params but we're in a different view type, ignore them
  if (hasFilterParams && isViewTypeMismatch) {
    // Clear filter params - they're from a different view type
    filterKeys.forEach(key => {
      delete params[key];
    });
  }
  
  // Re-check if we have filter params after potential clearing
  const hasFilterParamsAfterCheck = filterKeys.some(key => params[key]);
  
  // Use signal's appliedFilters as fallback ONLY if URL has no filter params
  // AND we're in the same view type (or no view type mismatch)
  // This ensures filters persist when switching tabs within the same view
  // URL params always take precedence
  const signalFilters = organizationsListSignal.value.appliedFilters || {};
  if (!hasFilterParamsAfterCheck && Object.keys(signalFilters).length > 0 && !isViewTypeMismatch) {
    // Merge signal filters into params, but only the filter keys
    filterKeys.forEach(key => {
      if (signalFilters[key]) {
        params[key] = signalFilters[key];
      }
    });
  }

  if (params.name) {
    where.organization.name = { contains: params.name, mode: 'insensitive' };
  }

  if (params.email) {
    where.organization.email = { contains: params.email, mode: 'insensitive' };
  }

  if (params.contactEmail) {
    if (portalType === PORTAL_TYPES.business) {
      where.organization.contactEmail = {
        contains: params.contactEmail,
        mode: 'insensitive',
      };
    } else {
      where.organization.email = {
        contains: params.contactEmail,
        mode: 'insensitive',
      };
    }
  }

  if (params.contactName) {
    where.organization.contactName = {
      contains: params.contactName,
      mode: 'insensitive',
    };
  }

  if (params.id) {
    where.organization.id = { in: [Number(params.id)] };
  }

  const businessMinorityConditions = [];
  if (
    portalType === PORTAL_TYPES.business &&
    params.veteranOwned
  ) {
    const value = params.veteranOwned === 'Yes' ? 'YES' : 'NO';
    businessMinorityConditions.push({
      minorityStatus: { path: ['isVeteranOwned'], equals: value },
    });
  }
  if (
    portalType === PORTAL_TYPES.business &&
    params.womanOwned
  ) {
    const value = params.womanOwned === 'Yes' ? 'YES' : 'NO';
    businessMinorityConditions.push({
      minorityStatus: { path: ['isWomanOwned'], equals: value },
    });
  }
  if (
    portalType === PORTAL_TYPES.business &&
    params.minorityOwned
  ) {
    const value = params.minorityOwned === 'Yes' ? 'YES' : 'NO';
    businessMinorityConditions.push({
      minorityStatus: { path: ['isMinorityOwned'], equals: value },
    });
  }

  // Only add the portal-specific where clause if there are actual filter conditions
  // An empty object can interfere with Prisma's include behavior
  const hasFilters =
    Object.keys(where.organization).length > 0 ||
    businessMinorityConditions.length > 0;

  if (hasFilters) {
    if (portalType === PORTAL_TYPES.business) {
      where.business = { ...where.organization };
      if (businessMinorityConditions.length > 0) {
        const minorityClause =
          businessMinorityConditions.length === 1
            ? businessMinorityConditions[0]
            : { AND: businessMinorityConditions };
        where.business =
          Object.keys(where.organization).length > 0
            ? { AND: [where.business, minorityClause] }
            : minorityClause;
      }
    } else if (portalType === PORTAL_TYPES.lender) {
      where.lender = where.organization;
    } else if (portalType === PORTAL_TYPES.vc) {
      where.ventureCapitalFirm = where.organization;
    }
    delete where.organization;
  } else {
    // No filters, return empty object to avoid interfering with includes
    return {};
  }

  return where;
};

export const handleProgramMembershipLinkClick = () => {
  organizationDetailsSignal.update({
    modalVisible: false,
  });
};

export const getRow = (account) => {
  const record =
    account.lender || account.business || account.ventureCapitalFirm;

  return {
    id: record?.id,
    name: record?.name || '-',
    contactName: record?.contactName || '-',
    contactEmail:
      record?.email?.toLowerCase() ||
      record?.contactEmail?.toLowerCase() ||
      '-',
    status: titleCaseString(getStatus(account)),
    need:
      record?.assistance?.desiredAssistanceTypes
        ?.map((t) => camelCaseToTitleText(t))
        .join(', ') || 'Unknown',
    createdDate: DateTime.fromISO(account.createdDate).toFormat('DD'),
  };
};

export const handleDetailClick = (record, newPath) => {
  const { PORTAL_TYPES } = $appSettings.value.constants;
  let organization;
  switch (record.portalType) {
    case PORTAL_TYPES.business:
      organization = record.business;
      break;
    case PORTAL_TYPES.lender:
      organization = record.lender;
      break;
    case PORTAL_TYPES.vc:
      organization = record.ventureCapitalFirm;
      break;
    default:
      break;
  }

  history.push(`${newPath}/${organization.id}`);
};

function debounce(func, delay) {
  let timeoutId;

  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

const fetchOrganizations = async (name) => {
  try {
    shareOrganizationSignal.update({
      fetching: true,
      organizations: [],
      selectedOrganization: null,
    });
    const where = { name: { contains: name, mode: 'insensitive' } };
    const options = {
      where,
      include: {
        account: true,
      },
    };

    const [lenders, ventureCapitalFirms] = await Promise.all([
      api.get({ path: '/lenders', options }),
      api.get({ path: '/ventureCapitalFirms', options }),
    ]);

    const organizations = [...lenders, ...ventureCapitalFirms].sort((a, b) =>
      a.name.localeCompare(b.name.localeCompare())
    );

    return shareOrganizationSignal.update({ organizations });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      message: error.message,
      error,
    });
  } finally {
    shareOrganizationSignal.update({ fetching: false });
  }
};

const debouncedFetchOrganizations = debounce(fetchOrganizations, 800);

export const handleShareModalOrganizationNameChange = (organizationName) => {
  shareOrganizationSignal.update({ organizationName });
  if (organizationName.length > 2) {
    return debouncedFetchOrganizations(organizationName);
  } else {
    return shareOrganizationSignal.update({ organizations: [] });
  }
};

export const showShareOrganizationModal = (selectedBusiness) =>
  shareOrganizationSignal.update({ selectedBusiness, modalVisible: true });

export const hideShareOrganizationModal = () =>
  shareOrganizationSignal.update({
    organizationName: '',
    selectedBusiness: null,
    modalVisible: false,
    organizations: [],
  });

export const handleClickOrganization = (organization) => {
  const { selectedOrganization } = shareOrganizationSignal.value;

  if (
    selectedOrganization?.id === organization.id &&
    selectedOrganization?.account.portalType === organization.account.portalType
  ) {
    shareOrganizationSignal.update({ selectedOrganization: null });
  } else {
    shareOrganizationSignal.update({ selectedOrganization: organization });
  }
};

export const shareBusinessWithOrganization = async () => {
  try {
    loaderSignal.update({ isContentLoading: true, message: 'Submitting ' });
    const { selectedBusiness, selectedOrganization } =
      shareOrganizationSignal.value;
    let body;

    if (selectedOrganization.account.portalType === PORTAL_TYPES.LENDER) {
      body = {
        where: {
          lenderId_businessId: {
            lenderId: selectedOrganization.id,
            businessId: selectedBusiness.id,
          },
        },
        create: {
          lenderId: selectedOrganization.id,
          businessId: selectedBusiness.id,
        },
        update: {
          updatedDate: new Date().toISOString(),
        },
      };
    } else {
      body = {
        where: {
          ventureCapitalFirmId_businessId: {
            ventureCapitalFirmId: selectedOrganization.id,
            businessId: selectedBusiness.id,
          },
        },
        create: {
          ventureCapitalFirmId: selectedOrganization.id,
          businessId: selectedBusiness.id,
        },
        update: {
          updatedDate: new Date().toISOString(),
        },
      };
    }

    await api.put({ path: '/businessSuggestions', body });

    alertSignal.update({
      variant: 'success',
      type: 'notification',
      message: 'Successfully shared business',
    });

    return hideShareOrganizationModal();
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      message: error.message,
      error,
    });
  } finally {
    loaderSignal.update({ isContentLoading: false });
  }
};

export const downloadSignupReport = async () => {
  await api.get({
    path: '/businesses/signup-report',
    isFileDownload: true,
  });
};
